import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { facilitators } from '../data/facilitators';
import { Search, MapPin, ArrowRight, CheckCircle2, Plane } from 'lucide-react';
export function Facilitators() {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState<string>('All');
  const [selectedService, setSelectedService] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const countries = useMemo(() => ['All', ...Array.from(new Set(facilitators.map(f => f.country))).sort()], []);
  const services = useMemo(() => ['All', ...Array.from(new Set(facilitators.flatMap(f => f.services))).sort()], []);
  const filteredFacilitators = useMemo(() => {
    return facilitators.filter(facilitator => {
      const matchesCountry = selectedCountry === 'All' || facilitator.country === selectedCountry;
      const matchesService = selectedService === 'All' || facilitator.services.includes(selectedService);
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch = query === '' || facilitator.name.toLowerCase().includes(query) || facilitator.city.toLowerCase().includes(query) || facilitator.services.some(s => s.toLowerCase().includes(query));
      return matchesCountry && matchesService && matchesSearch;
    });
  }, [selectedCountry, selectedService, searchQuery]);
  const clearFilters = () => {
    setSelectedCountry('All');
    setSelectedService('All');
    setSearchQuery('');
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-602">
      <Helmet data-id="element-603">
        <title data-id="element-604">Partner Facilitators | Affordable Medical Tourism</title>
        <meta name="description" content="Connect with trusted local medical tourism facilitators who provide on-the-ground support, translation, logistics, and recovery care for your medical journey." data-id="element-605" />
        <link rel="canonical" href="https://affordablemedicaltourism.com/facilitators" data-id="element-606" />
      </Helmet>

      <AppHeader data-id="element-607" />

      <main className="flex-1 py-20 px-6 md:px-12 max-w-7xl mx-auto w-full" data-id="element-608">
        <div className="text-center mb-12" data-id="element-609">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-610">
            Partner Facilitators
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-611">
            Our trusted on-the-ground partners make your journey seamless. From
            airport transfers and translation to recovery retreats and visa
            assistance, they handle the logistics so you can focus on healing.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-8 space-y-6" data-id="element-612">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-613">
            <div data-id="element-614">
              <label htmlFor="facilitator-country" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-615">
                Country
              </label>
              <select id="facilitator-country" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-616">
                {countries.map(country => <option key={country} value={country} data-id="element-617">
                    {country}
                  </option>)}
              </select>
            </div>

            <div data-id="element-618">
              <label htmlFor="facilitator-service" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-619">
                Service
              </label>
              <select id="facilitator-service" value={selectedService} onChange={e => setSelectedService(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-620">
                {services.map(service => <option key={service} value={service} data-id="element-621">
                    {service}
                  </option>)}
              </select>
            </div>

            <div data-id="element-622">
              <label htmlFor="facilitator-search" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-623">
                Search
              </label>
              <div className="relative" data-id="element-624">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-625" />
                <input id="facilitator-search" type="text" placeholder="Search facilitators or services" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-626" />
              </div>
            </div>
          </div>

          <p className="text-sm text-slate-500" aria-live="polite" data-id="element-627">
            Showing {filteredFacilitators.length} of {facilitators.length}{' '}
            facilitators
          </p>
        </div>

        {/* Grid */}
        {filteredFacilitators.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-628">
            {filteredFacilitators.map(facilitator => <Link key={facilitator.slug} to={`/facilitators/${facilitator.slug}`} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-md transition-shadow flex flex-col group focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-629">
                <div className="relative h-48 overflow-hidden" data-id="element-630">
                  <img src={facilitator.image} alt={`${facilitator.name} in ${facilitator.city}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-631" />
                </div>
                <div className="p-6 flex-1 flex flex-col" data-id="element-632">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2" data-id="element-633">
                    <MapPin className="w-4 h-4" data-id="element-634" />
                    {facilitator.city}, {facilitator.country}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-700 transition-colors" data-id="element-635">
                    {facilitator.name}
                  </h2>
                  <div className="space-y-2 mb-6" data-id="element-636">
                    {facilitator.services.slice(0, 3).map(service => <div key={service} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-637">
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" data-id="element-638" />
                        {service}
                      </div>)}
                    {facilitator.services.length > 3 && <div className="text-sm text-slate-500 pl-6" data-id="element-639">
                        +{facilitator.services.length - 3} more services
                      </div>}
                  </div>
                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-640">
                    <div className="text-sm font-medium text-slate-900" data-id="element-641">
                      {facilitator.totalPackagePrice}
                    </div>
                    <span className="text-blue-700 font-medium flex items-center gap-1 text-sm group-hover:translate-x-1 transition-transform" data-id="element-642">
                      View profile <ArrowRight className="w-4 h-4" data-id="element-643" />
                    </span>
                  </div>
                </div>
              </Link>)}
          </div> : <div className="text-center py-20" data-id="element-644">
            <p className="text-xl text-slate-600 mb-4" data-id="element-645">
              No facilitators found matching your criteria.
            </p>
            <Button variant="outline" onClick={clearFilters} data-id="element-646">
              Clear filters
            </Button>
          </div>}

        {/* Conversion band */}
        <section className="mt-20 bg-blue-700 rounded-3xl px-8 py-14 md:px-16 text-center" data-id="element-647">
          <Plane className="w-10 h-10 text-blue-200 mx-auto mb-6" data-id="element-648" />
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-649">
            Let us coordinate your trip end to end
          </h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-650">
            Share your destination and needs. A care specialist will pair you
            with a vetted local facilitator and arrange your logistics. There is
            no cost to ask.
          </p>
          <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50" onClick={() => navigate('/contact')} data-id="element-651">
            Get your free quote
          </Button>
        </section>
      </main>

      <AppFooter data-id="element-652" />
    </div>;
}